<template lang="pug">
  .rma-print-return(ref="root")
    section
      h4 {{ __('Your RMA form is ready') }}
      Box
        h5 {{ __('RMA ID') }} {{ rmaId || '' }}
        p.info(v-translate)
          | Include your printed RMA Form in the box with your return items. Please contact us at
          | {{ rogue.site.support.phone }} or &nbsp;
          a(:href="`mailto:${rogue.site.support.email}`") {{ rogue.site.support.email }}
          | &nbsp; with any questions.
    section
      h4 {{ __('Download or Email RMA') }}
      Row(no-gutters)
        Column(tablet="5" desktop="4")
          Field
            RogueButton(
              :text="__('Download RMA Form')"
              :loading="downloading"
              @click="downloadRMA"
              width="full"
              :disabled="disableForm"
            )
          RogueInput.form-field.field-email(
            label="Email"
            type="email"
            required
            placeholder="Ex: johndoe@roguefitness.com"
            v-model="email"
            :disabled="disableForm"
          )
          RogueButton(
            :loading="sending"
            @click="emailRMA"
            width="full"
            :disabled="disableForm"
          )
            span.email-success(v-if="status === 'success'")
              Icon( name="check" fill="green" )
              | {{__('Form Emailed')}}
            span(v-else) {{ __('Email RMA Form') }}
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useRogue } from '@/js/services/Rogue'
import { getPdfDocument } from '@/js/helpers/pdf'
import { validate } from '@/js/helpers/validate-fns'

const props = defineProps({
  order: {
    type: Object,
    default() {
      return {}
    },
  },
  rma: {
    type: Object,
    default() {
      return {}
    },
  },
})

const rogue = useRogue()
const route = useRoute()

const root = ref<HTMLElement | undefined>(undefined)
const sending = ref(false)
const downloading = ref(false)
const email = ref(props.order?.customer?.email || '')
const status = ref('')

const rmaId = computed(() => route.params?.rmaNumber || props.rma?.rmaNumber || null)
const token = computed(() => props.order?.token || null)
const disableForm = computed(() => !rmaId.value || !token.value)

const rmaDate = computed(() => {
  const dateParam = Array.isArray(route.params?.dateIssued) ? route.params.dateIssued[0] : route.params.dateIssued
  const date = dateParam ? new Date(dateParam) : new Date() 

  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()

  return `${date.getFullYear()}-${month}-${day}`
})

async function downloadRMA() {
  try {
    downloading.value = true

    const response = await getPdfDocument(`/svc_rma/rma/${rmaId.value}/print`,'POST', { token: token.value }, {}, rogue)

    if (response?.url) {
      const link = document.createElement('a')
      link.download = `Rogue RMA ${rmaId.value} - ${rmaDate.value}.pdf`
      link.href = response.url
      link.click()
    }
  } catch (e) {
    rogue.log.error('RMA: Unable to fetch document', e)
  } finally {
    downloading.value = false
  }
}

async function emailRMA() {
  if (root.value && !validate(root.value)) {
    return
  }

  try {
    sending.value = true
    const response = await rogue.fetch<{ success?: { [key: string]: string } }>(`/svc_rma/rma/${rmaId.value}/email/${encodeURIComponent(email.value)}`,'POST', { token: token.value })
    status.value = response.success ? 'success' : 'error'
  } catch (e) {
    rogue.log.error('RMA: Unable to email document', e)
    status.value = 'error'
  } finally {
    sending.value = false
  }
}
</script>

<style lang="scss" scoped>
.email-success {
  display: flex;
  align-items: center;
  justify-content: center;

  .icon {
    margin-right: $space-xxs;
  }
}
.info {
  font-size: $size-xs;
  line-height: $line-height-m;

  @include not-mobile {
    font-size: $size-s;
  }
}
.field:not(.form-field) {
  margin-bottom: $space-s;
}
.field-email {
  margin-bottom: $space-xxs;
}
</style>
